import { useEffect, useRef, useCallback, useState } from 'react';

export function useAutoSaveOnExit(saveFunction, isEnabled = true, autoSaveIntervalMs = 30000) {
  const hasChangesRef = useRef(false);
  const saveRef = useRef(saveFunction);
  const isSavingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    saveRef.current = saveFunction;
  }, [saveFunction]);

  const markDirty = useCallback(() => {
    hasChangesRef.current = true;
  }, []);

  const markClean = useCallback(() => {
    hasChangesRef.current = false;
  }, []);

  const save = useCallback(async (force = false) => {
    if ((!hasChangesRef.current && !force) || isSavingRef.current || !isEnabled) {
      return false;
    }
    
    try {
      isSavingRef.current = true;
      setIsSaving(true);
      await saveRef.current();
      hasChangesRef.current = false;
      return true;
    } catch (error) {
      console.error('Auto-save failed:', error);
      return false;
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [isEnabled]);

  const saveAndWait = useCallback(async () => {
    if (isSavingRef.current) {
      return new Promise((resolve) => {
        const TIMEOUT_MS = 10000;
        let checkInterval;

        const cleanup = () => {
          clearInterval(checkInterval);
        };

        const timeoutId = setTimeout(() => {
          cleanup();
          resolve(false);
        }, TIMEOUT_MS);

        checkInterval = setInterval(() => {
          if (!isSavingRef.current) {
            clearTimeout(timeoutId);
            cleanup();
            resolve(true);
          }
        }, 100);
      });
    }
    return save(true);
  }, [save]);

  const hasChanges = useCallback(() => hasChangesRef.current, []);

  useEffect(() => {
    if (!isEnabled || !autoSaveIntervalMs) return;

    const intervalId = setInterval(() => {
      if (hasChangesRef.current && !isSavingRef.current) {
        save();
      }
    }, autoSaveIntervalMs);

    return () => clearInterval(intervalId);
  }, [isEnabled, autoSaveIntervalMs, save]);

  useEffect(() => {
    if (!isEnabled) return;

    const handleBeforeUnload = (e) => {
      if (hasChangesRef.current) {
        e.preventDefault();
        e.returnValue = 'Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEnabled]);

  useEffect(() => {
    return () => {
      if (hasChangesRef.current && !isSavingRef.current) {
        saveRef.current().catch(err => console.error('Save on unmount failed:', err));
      }
    };
  }, []);

  return { markDirty, markClean, save, saveAndWait, hasChanges, isSaving };
}

export default useAutoSaveOnExit;
